import type { SessionSnapshot } from "./types";

const SESSION_KEY = "refinement-canvas.auth.session";
const SIGNED_OUT_KEY = "refinement-canvas.auth.signed-out";
const SESSION_CHANGED_EVENT_NAME = "refinement-canvas:auth-session-changed";
export type SignedOutState = "none" | "pending" | "failed" | "completed";
let signedOutInMemory: SignedOutState = "none";

// localStorage への書き込みは即時 storage イベントを生成しないため、
// 同一タブ内で saveSessionSnapshot / clearSessionSnapshot をトリガーした側からも
// 購読者へ通知できるようカスタムイベントを併用する。
function notifySessionChanged(): void {
  window.dispatchEvent(new Event(SESSION_CHANGED_EVENT_NAME));
}

/**
 * SessionSnapshot を localStorage から読み出す。
 *
 * 取得できない場合や JSON が壊れている場合は undefined を返す。
 */
export function loadSessionSnapshot(): SessionSnapshot | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  let raw: string | null;
  try {
    raw = window.localStorage.getItem(SESSION_KEY);
  } catch {
    return undefined;
  }
  if (raw === null) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<SessionSnapshot> | null;
    if (
      parsed === null ||
      typeof parsed !== "object" ||
      typeof parsed.userId !== "string" ||
      typeof parsed.name !== "string" ||
      (parsed.source !== "meiden-connect" && parsed.source !== "mock")
    ) {
      return undefined;
    }
    return {
      userId: parsed.userId,
      name: parsed.name,
      email: typeof parsed.email === "string" ? parsed.email : undefined,
      affiliation: typeof parsed.affiliation === "string" ? parsed.affiliation : undefined,
      avatarImageUrl: typeof parsed.avatarImageUrl === "string" ? parsed.avatarImageUrl : undefined,
      source: parsed.source,
      globalPermissionIds: Array.isArray(parsed.globalPermissionIds)
        ? parsed.globalPermissionIds.filter((value): value is string => typeof value === "string")
        : undefined,
    };
  } catch {
    return undefined;
  }
}

/**
 * SessionSnapshot を localStorage に保存し、購読者へ通知する。
 *
 * 画面起動時にローカルストレージから即時に読み出せるよう、idp-file: 形式の
 * アバター URL や Blob URL は除外して保存する。
 */
export function saveSessionSnapshot(snapshot: SessionSnapshot): void {
  clearSignedOut();
  if (typeof window === "undefined") {
    return;
  }

  // Blob URL / idp-file: / 巨大な data URL は再起動後の即時利用ができないため、
  // 永続化対象から除外する。data URL もここで弾いて、ヘッダー送信用の小さい URL は
  // ランタイム側で再生成させる。
  const persistableAvatarImageUrl =
    typeof snapshot.avatarImageUrl === "string" &&
    !snapshot.avatarImageUrl.startsWith("blob:") &&
    !snapshot.avatarImageUrl.startsWith("idp-file:") &&
    !snapshot.avatarImageUrl.startsWith("data:")
      ? snapshot.avatarImageUrl
      : undefined;

  const persistable: SessionSnapshot = {
    ...snapshot,
    avatarImageUrl: persistableAvatarImageUrl,
  };

  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(persistable));
  } catch {
    // ストレージが無効な環境でも、メモリ上のサインイン処理は継続させる。
  }
  notifySessionChanged();
}

/**
 * SessionSnapshot を localStorage から削除し、購読者へ通知する。
 */
export function clearSessionSnapshot(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // ストレージが無効な環境でも、React state のクリア処理は継続させる。
  }
  notifySessionChanged();
}

/**
 * 利用者が明示的にサインアウトしたことを記録する。
 *
 * localStorage が使えない場合は、IdP 往復をまたいで残る sessionStorage、
 * 最後に同一ドキュメント内のメモリへフォールバックする。
 */
export function markSignedOut(): void {
  writeSignedOutState("pending");
}

/** IdP へ遷移できなかったサインアウト試行を記録する。 */
export function markSignOutFailed(): void {
  writeSignedOutState("failed");
}

/** IdP から復帰し、グローバルサインアウトが完了したことを記録する。 */
export function markSignOutCompleted(): void {
  writeSignedOutState("completed");
}

function writeSignedOutState(state: Exclude<SignedOutState, "none">): void {
  // Web Storage の可用性が途中で変化しても、同一ドキュメントでは状態を失わないよう保持する。
  signedOutInMemory = state;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(SIGNED_OUT_KEY, state);
      notifySessionChanged();
      return;
    } catch {
      // 次の保存先を試す。
    }
    try {
      window.sessionStorage.setItem(SIGNED_OUT_KEY, state);
      notifySessionChanged();
      return;
    } catch {
      // 最後にメモリへ保持する。
    }
  }
  if (typeof window !== "undefined") {
    notifySessionChanged();
  }
}

/** サインアウト済みの記録がいずれかの保存先に存在するかを返す。 */
export function isSignedOut(): boolean {
  return getSignedOutState() !== "none";
}

/** サインアウト処理の永続状態を返す。 */
export function getSignedOutState(): SignedOutState {
  if (typeof window !== "undefined") {
    try {
      const localState = normalizeSignedOutState(window.localStorage.getItem(SIGNED_OUT_KEY));
      if (localState !== "none") {
        return localState;
      }
    } catch {
      // 次の保存先を確認する。
    }
    try {
      const sessionState = normalizeSignedOutState(window.sessionStorage.getItem(SIGNED_OUT_KEY));
      if (sessionState !== "none") {
        return sessionState;
      }
    } catch {
      // メモリ上の記録を確認する。
    }
  }
  return signedOutInMemory;
}

function normalizeSignedOutState(value: string | null): SignedOutState {
  if (value === "pending" || value === "failed" || value === "completed") {
    return value;
  }
  // 旧形式の真偽値は、IdP 完了を断定できないため pending として安全側に扱う。
  return value === "true" ? "pending" : "none";
}

/** サインイン成功時に、すべての保存先からサインアウト済みの記録を解除する。 */
export function clearSignedOut(): void {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(SIGNED_OUT_KEY);
    } catch {
      // 他の保存先の解除は継続する。
    }
    try {
      window.sessionStorage.removeItem(SIGNED_OUT_KEY);
    } catch {
      // メモリ上の記録の解除は継続する。
    }
  }
  signedOutInMemory = "none";
}

/**
 * 別タブでの localStorage 更新および同一タブでの save/clear を購読する。
 *
 * @param listener セッション情報が変化したときに呼ばれるコールバック
 * @returns 購読解除関数
 */
export function subscribeSessionSnapshot(listener: () => void): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const handleStorage = (event: StorageEvent): void => {
    if (event.key === SESSION_KEY || event.key === SIGNED_OUT_KEY || event.key === null) {
      listener();
    }
  };

  window.addEventListener("storage", handleStorage);
  window.addEventListener(SESSION_CHANGED_EVENT_NAME, listener);

  return () => {
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener(SESSION_CHANGED_EVENT_NAME, listener);
  };
}
